// Edge operations on the plot polygon (Stage 6d).
//
// All coordinates are in local metres — same frame as `store.plot.polygonM`.
// Every function here is pure (no mutation, no Konva, no DOM), so it's
// trivially testable and safe to call during render.

import { polygonEdges } from "./geometry"

export { polygonEdges }

// Length of an edge [a, b] in metres.
export function edgeLengthM([a, b]) {
  return Math.hypot(b[0] - a[0], b[1] - a[1])
}

// Midpoint of an edge [a, b] in metres — used as the label anchor.
export function edgeMidpointM([a, b]) {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
}

// Angle of the edge vector (a → b) in radians, CCW from +x. Useful for
// deciding whether to rotate the label text to lie along the edge or
// keep it horizontal (we fall back to horizontal when |angle| > 45°).
export function edgeAngleRad([a, b]) {
  return Math.atan2(b[1] - a[1], b[0] - a[0])
}

// Resize the i-th edge of a polygon to `newLenM` by moving one of its two
// endpoints along the edge axis. `anchor` selects which endpoint stays put:
//   "a" — first vertex is fixed, second one slides → edge extends/shrinks
//          from b's side.
//   "b" — second vertex is fixed, first one slides.
//
// Neighbouring edges stretch/shrink automatically because their other
// endpoint is shared with the moved vertex. Non-neighbour edges are
// untouched. The polygon returned is a new array (immutable input).
//
// Returns null if inputs are invalid (bad index, zero-length edge, bad
// length). Callers must validate the result against `wouldSelfIntersect`
// before committing.
export function resizeEdgeAlongAxis(polygonM, edgeIdx, newLenM, anchor = "b") {
  if (!Array.isArray(polygonM) || polygonM.length < 3) return null
  const edges = polygonEdges(polygonM)
  if (edgeIdx < 0 || edgeIdx >= edges.length) return null
  if (!isFinite(newLenM) || newLenM <= 0) return null

  const [a, b] = edges[edgeIdx]
  const curLen = edgeLengthM([a, b])
  if (curLen === 0) return null

  const ux = (b[0] - a[0]) / curLen
  const uy = (b[1] - a[1]) / curLen

  // Work against the de-duplicated ring (last vertex may duplicate first).
  const ring = stripClosure(polygonM)
  const next = ring.map((v) => [v[0], v[1]])
  const aIdx = edgeIdx
  const bIdx = (edgeIdx + 1) % ring.length

  if (anchor === "a") {
    next[bIdx] = [a[0] + ux * newLenM, a[1] + uy * newLenM]
  } else {
    next[aIdx] = [b[0] - ux * newLenM, b[1] - uy * newLenM]
  }
  // Preserve the original closure shape (ring was closed if input was).
  return wasClosed(polygonM) ? [...next, [next[0][0], next[0][1]]] : next
}

// True if any non-adjacent pair of edges crosses. Edges sharing a vertex
// are skipped (they meet there by design). Uses the segment-intersection
// predicate already defined for norms geometry — n² for n edges, but
// n ≤ ~50 for any realistic plot, so it's ≤2500 pairs (~0.5 ms).
export function wouldSelfIntersect(polygonM) {
  if (!Array.isArray(polygonM) || polygonM.length < 4) return false
  const ring = stripClosure(polygonM)
  const n = ring.length
  if (n < 4) return false
  for (let i = 0; i < n; i++) {
    const a1 = ring[i], a2 = ring[(i + 1) % n]
    for (let j = i + 1; j < n; j++) {
      // Skip neighbouring edges (they share a vertex).
      if (j === i + 1) continue
      if (i === 0 && j === n - 1) continue
      const b1 = ring[j], b2 = ring[(j + 1) % n]
      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

function stripClosure(polygonM) {
  return wasClosed(polygonM) ? polygonM.slice(0, -1) : polygonM.slice()
}

function wasClosed(polygonM) {
  const n = polygonM.length
  if (n < 2) return false
  const first = polygonM[0], last = polygonM[n - 1]
  return first[0] === last[0] && first[1] === last[1]
}

// Inlined from geometry.js (the exported `segmentsIntersect` is private
// there). Keeping a local copy avoids a circular export and makes
// wouldSelfIntersect self-contained.
function segmentsIntersect(a1, a2, b1, b2) {
  const o1 = orient(a1, a2, b1)
  const o2 = orient(a1, a2, b2)
  const o3 = orient(b1, b2, a1)
  const o4 = orient(b1, b2, a2)
  if (o1 !== o2 && o3 !== o4) return true
  // Colinear-touching counts as intersection for plot-edit validation —
  // it would turn two edges into a degenerate overlapping segment.
  if (o1 === 0 && onSegment(a1, a2, b1)) return true
  if (o2 === 0 && onSegment(a1, a2, b2)) return true
  if (o3 === 0 && onSegment(b1, b2, a1)) return true
  if (o4 === 0 && onSegment(b1, b2, a2)) return true
  return false
}

function orient(a, b, c) {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
  if (v > 0) return 1
  if (v < 0) return -1
  return 0
}

function onSegment(a, b, c) {
  return Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) &&
         Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1])
}
